/**
 * YDM Platform Brand Configuration
 *
 * Canonical brand identity for the YDM SaaS Platform. Mirrors the official
 * 2025 brand guideline (verified against the ydm-design skill at
 * `C:/Users/nutta/Documents/ydm-thailand/skills/ydm-design/SKILL.md` and tokens
 * JSON). When the guideline updates, refresh this file AND `tailwind.config.ts`
 * together — they're the design source for every YDM project.
 *
 * Brand essentials:
 *   - Primary: Yellow #FFFF00 (70%) + Black #000000 (30%)
 *   - Secondary IDEAS palette (~10% accents, NEVER on logo):
 *     orange #F7941D / green #94F53A / blue #4667FF / cyan #00BEF9 / pink #FE4FFF
 *   - Typeface: Prompt (Google Fonts, Thai + Latin)
 *   - Logo: lowercase `ydm` + `»» arrow` motion mark
 *   - Slogan: MAKE IT UNSTOPPABLE
 *   - Voice: Energetic · Smart · Playful · Accessible
 */

export interface BrandConfig {
  brandName: string;
  brandFullName: string;
  platformName: string;
  slogan: string;
  domain: string;

  contact: {
    email: string;
    phone: string;
    website: string;
  };

  /**
   * YDM uses a 2-color primary system (yellow + black). The rest of the
   * fields are secondary / semantic so existing call sites can keep using
   * `colors.primary` etc. — `primary` resolves to the brand yellow.
   */
  colors: {
    primary: string;       // YDM yellow — the dominant brand color
    primaryDark: string;   // YDM black — partner color in the 70/30 system
    accentOrange: string;  // IDEAS — I
    accentGreen: string;   //         D
    accentBlue: string;    //         E
    accentCyan: string;    //         A
    accentPink: string;    //         S
    success: string;
    warning: string;
    error: string;
    info: string;
  };

  ratio: {
    yellow: number;        // 0.7
    black: number;         // 0.3
    secondaryAccent: number; // 0.1
  };

  typography: {
    fontFamily: string;
    fallback: string[];
  };

  logo: {
    // Variants under /public/logo. See public/logo/README.md.
    yellowOnBlack: string;     // ydm-01.png — primary mark
    yellowTransparent: string; // ydm-04.png — over dark photos
    blackTransparent: string;  // ydm-08.png — over yellow/light
    whiteTransparent: string;  // ydm-12.png — over colored photos
    yellowTaglineOnBlack: string;  // ydm-02.png — full lockup on dark
    blackTaglineOnYellow: string;  // ydm-05.png — full lockup hero on yellow
    favicon: string;
  };

  social: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    youtube?: string;
    lineOA?: string;
  };

  saasProducts: SaasProductConfig[];
}

export interface SaasProductConfig {
  id: string;
  name: string;
  nameTH: string;
  description: string;
  descriptionTH: string;
  domain: string;
  icon: string;
  color: string;
  features: string[];
  featuresTH: string[];
}

export const BRAND_CONFIG: BrandConfig = {
  brandName: "ydm",
  brandFullName: "YDM Thailand",
  platformName: "YDM Media Intelligence",
  slogan: "MAKE IT UNSTOPPABLE",
  domain: "ydm.family",

  contact: {
    email: "contact@ydm.family",
    phone: "+66 XX XXX XXXX",
    website: "https://ydm.family",
  },

  colors: {
    primary: "#FFFF00",       // YDM Yellow — Process Yellow C
    primaryDark: "#000000",   // YDM Black — Process Black C
    accentOrange: "#F7941D",  // I in IDEAS
    accentGreen:  "#94F53A",  // D
    accentBlue:   "#4667FF",  // E
    accentCyan:   "#00BEF9",  // A
    accentPink:   "#FE4FFF",  // S
    success: "#22c55e",
    warning: "#F7941D",       // re-use brand orange so warnings stay on-brand
    error:   "#DC2626",
    info:    "#4667FF",       // re-use brand blue
  },

  ratio: {
    yellow: 0.7,
    black: 0.3,
    secondaryAccent: 0.1,
  },

  typography: {
    fontFamily: "Prompt",
    fallback: ["Prompt", "Noto Sans Thai", "Arial", "sans-serif"],
  },

  logo: {
    yellowOnBlack:        "/logo/ydm-01.png",
    yellowTransparent:    "/logo/ydm-04.png",
    blackTransparent:     "/logo/ydm-08.png",
    whiteTransparent:     "/logo/ydm-12.png",
    yellowTaglineOnBlack: "/logo/ydm-02.png",
    blackTaglineOnYellow: "/logo/ydm-05.png",
    favicon:              "/favicon.svg",
  },

  social: {
    facebook: "https://www.facebook.com/ydmfamily",
    instagram: "https://www.instagram.com/ydmfamily",
    linkedin: "https://www.linkedin.com/company/ydmfamily",
    youtube: "https://www.youtube.com/@ydmfamily",
    lineOA: "https://line.me/R/ti/p/@ydm-family",
  },

  saasProducts: [
    {
      id: "content_generator",
      name: "AI Content Generator",
      nameTH: "AI สร้างเนื้อหา",
      description: "AI-powered content creation for digital marketing",
      descriptionTH: "สร้างเนื้อหาการตลาดดิจิทัลด้วย AI",
      domain: "content.ydm.family",
      icon: "✨",
      color: "#8B5CF6",
      features: [
        "Generate Thai marketing content in seconds",
        "Multi-channel support (Facebook, LINE, TikTok)",
        "Brand voice consistency",
        "Reference image integration",
      ],
      featuresTH: [
        "สร้างเนื้อหาการตลาดภาษาไทยในไม่กี่วินาที",
        "รองรับหลายช่องทาง (Facebook, LINE, TikTok)",
        "ความสอดคล้องของเสียงแบรนด์",
        "ผสานรวมภาพอ้างอิง",
      ],
    },
    {
      id: "media_intelligence",
      name: "Media Intelligence",
      nameTH: "สื่อสารอัจฉริยะ",
      description: "Marketing analytics and MMM (Marketing Mix Modeling)",
      descriptionTH: "การวิเคราะห์การตลาดและ MMM",
      domain: "analytics.ydm.family",
      icon: "📊",
      color: "#3B82F6",
      features: [
        "Multi-platform campaign tracking",
        "Marketing Mix Modeling (MMM)",
        "Real-time performance dashboards",
        "AI-powered recommendations",
      ],
      featuresTH: [
        "ติดตามแคมเปญหลายแพลตฟอร์ม",
        "Marketing Mix Modeling (MMM)",
        "แดชบอร์ดประสิทธิภาพแบบเรียลไทม์",
        "คำแนะนำที่ขับเคลื่อนด้วย AI",
      ],
    },
    {
      id: "customer_360",
      name: "Customer 360°",
      nameTH: "ข้อมูลลูกค้า 360°",
      description: "Unified customer view with interaction management",
      descriptionTH: "มุมมองลูกค้าแบบครบวงจรพร้อมจัดการการติดต่อ",
      domain: "customers.ydm.family",
      icon: "👥",
      color: "#10B981",
      features: [
        "360° customer view",
        "Interaction and engagement tracking",
        "Customer segmentation",
        "Communication history",
      ],
      featuresTH: [
        "มุมมองลูกค้าแบบ 360°",
        "ติดตามการมีปฏิสัมพันธ์และการมีส่วนร่วม",
        "การแบ่งกลุ่มลูกค้า",
        "ประวัติการติดต่อสื่อสาร",
      ],
    },
  ],
};

// Helper functions
export const getSaasProduct = (
  productId: string
): SaasProductConfig | undefined => {
  return BRAND_CONFIG.saasProducts.find((p) => p.id === productId);
};

export const getBrandColor = (
  colorKey: keyof BrandConfig["colors"],
  fallback = "#FFFF00"
): string => {
  return BRAND_CONFIG.colors[colorKey] || fallback;
};

export const formatCurrency = (
  amount: number,
  withSymbol = true,
  currency = "THB",
  fractionDigits = 2
): string => {
  const formatted = new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);

  if (currency === "THB") {
    return withSymbol ? `฿${formatted}` : formatted;
  }

  return withSymbol ? `${currency} ${formatted}` : formatted;
};

export const getLocalizedText = (
  enText: string,
  thText: string,
  lang: "th" | "en" = "th"
): string => {
  return lang === "th" ? thText : enText;
};

export default BRAND_CONFIG;
